import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { Json, TablesInsert } from "@/integrations/supabase/types";
import type { InterviewPrepQuestion } from "./readInterviewPrep";

export interface InterviewPrepQuestionEntry {
	question: string;
	recruiter_note: string;
}

// `questions` may contain:
//  - legacy string entries (plain note keyed by question1..N)
//  - intermediate { question, note } entries
//  - current { question, recruiter_note } entries
// Readers should use `getQuestionNoteValue` to normalize.
type LegacyQuestionEntry = { question?: string; note?: string };
export interface InterviewPrepNotes {
	// Frozen snapshot of the question list at the moment the recruiter first
	// interacted with this match. Once set, it becomes the source of truth for
	// the question list, and subsequent ai_analysis rewrites are ignored.
	questions_snapshot?: InterviewPrepQuestion[];
	// Keys of questions the recruiter has explicitly dismissed.
	ignored_questions?: string[];
	questions?: Record<
		string,
		InterviewPrepQuestionEntry | LegacyQuestionEntry | string
	>;
	strengths?: Record<string, string>;
	gaps?: Record<string, string>;
}

export const getQuestionNoteValue = (
	entry: InterviewPrepQuestionEntry | LegacyQuestionEntry | string | undefined
): string => {
	if (!entry) return "";
	if (typeof entry === "string") return entry;
	if ("recruiter_note" in entry && typeof entry.recruiter_note === "string")
		return entry.recruiter_note;
	if ("note" in entry && typeof entry.note === "string")
		return entry.note;
	return "";
};

export type SaveState = "idle" | "saving" | "saved" | "error";

const DEBOUNCE_MS = 800;

const emptyNotes = (): InterviewPrepNotes => ({
	questions: {},
	strengths: {},
	gaps: {},
});

export const useMatchInterviewPrep = (matchId: string | undefined) => {
	const { user } = useAuth();
	const [notes, setNotes] = useState<InterviewPrepNotes>(emptyNotes());
	const [generalNotes, setGeneralNotes] = useState<string>("");
	const [questionsSnapshot, setQuestionsSnapshot] = useState<
		InterviewPrepQuestion[] | null
	>(null);
	const [updatedAt, setUpdatedAt] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [saveState, setSaveState] = useState<SaveState>("idle");

	const notesRef = useRef(notes);
	const generalNotesRef = useRef(generalNotes);
	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasRemoteRow = useRef(false);
	// Latest in-memory question list from the live ai_analysis. Set by the
	// parent via `setActiveQuestions`. Used to seed `questions_snapshot` on
	// the first edit if no snapshot exists yet.
	const activeQuestionsRef = useRef<InterviewPrepQuestion[]>([]);

	// Keep refs in sync for the debounced flush
	useEffect(() => {
		notesRef.current = notes;
	}, [notes]);
	useEffect(() => {
		generalNotesRef.current = generalNotes;
	}, [generalNotes]);

	// Initial load
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			if (!matchId) {
				setLoading(false);
				return;
			}
			setLoading(true);
			const { data, error } = await supabase
				.from("match_interview_prep")
				.select("id, match_id, notes, general_notes, updated_at")
				.eq("match_id", matchId)
				.maybeSingle();

			if (cancelled) return;

			if (error) {
				console.error("Error loading interview prep:", error);
				setLoading(false);
				return;
			}

			if (data) {
				hasRemoteRow.current = true;
				setUpdatedAt(data.updated_at ?? null);
				const loadedNotes = (data.notes as InterviewPrepNotes) || emptyNotes();
				const snapshot = Array.isArray(loadedNotes.questions_snapshot)
					? loadedNotes.questions_snapshot
					: null;
				setNotes({
					questions_snapshot: snapshot ?? undefined,
					ignored_questions: Array.isArray(loadedNotes.ignored_questions)
						? loadedNotes.ignored_questions
						: [],
					questions: loadedNotes.questions || {},
					strengths: loadedNotes.strengths || {},
					gaps: loadedNotes.gaps || {},
				});
				setQuestionsSnapshot(snapshot);
				setGeneralNotes(data.general_notes || "");
			} else {
				hasRemoteRow.current = false;
				setNotes(emptyNotes());
				setQuestionsSnapshot(null);
				setGeneralNotes("");
			}
			setLoading(false);
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [matchId]);

	const flush = useCallback(async () => {
		if (!matchId) return;
		setSaveState("saving");

		// Seed the question snapshot on the first write if the server row doesn't
		// already carry one. Parent keeps `activeQuestionsRef` up to date with
		// the current ai_analysis question list.
		const currentNotes: InterviewPrepNotes = { ...notesRef.current };
		const hasSnapshot =
			Array.isArray(currentNotes.questions_snapshot) &&
			currentNotes.questions_snapshot.length > 0;
		let seededSnapshot: InterviewPrepQuestion[] | null = null;
		if (!hasSnapshot && activeQuestionsRef.current.length > 0) {
			seededSnapshot = activeQuestionsRef.current;
			currentNotes.questions_snapshot = seededSnapshot;
			// Update refs + state so subsequent reads see the snapshot.
			notesRef.current = currentNotes;
		}

		const payload: TablesInsert<"match_interview_prep"> = {
			match_id: matchId,
			notes: currentNotes as unknown as Json,
			general_notes: generalNotesRef.current || null,
			updated_by: user?.id || null,
		};
		if (!hasRemoteRow.current)
			payload.created_by = user?.id || null;

		const { data: upsertData, error } = await supabase
			.from("match_interview_prep")
			.upsert(payload, { onConflict: "match_id" })
			.select("updated_at")
			.maybeSingle();

		if (error) {
			console.error("Error saving interview prep:", error);
			setSaveState("error");
			return;
		}
		hasRemoteRow.current = true;
		if (upsertData?.updated_at)
			setUpdatedAt(upsertData.updated_at);
		if (seededSnapshot) {
			setNotes(currentNotes);
			setQuestionsSnapshot(seededSnapshot);
		}
		setSaveState("saved");
		// Reset to idle after a short delay so the indicator can fade
		setTimeout(() => {
			setSaveState((s) => (s === "saved" ? "idle" : s));
		}, 1500);
	}, [matchId, user?.id]);

	const schedule = useCallback(() => {
		if (debounceTimer.current) clearTimeout(debounceTimer.current);
		debounceTimer.current = setTimeout(() => {
			void flush();
		}, DEBOUNCE_MS);
	}, [flush]);

	// Flush any pending edit on unmount
	useEffect(() => {
		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
				void flush();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateQuestionNote = useCallback(
		(questionKey: string, questionText: string, value: string) => {
			setNotes((prev) => ({
				...prev,
				questions: {
					...(prev.questions || {}),
					[questionKey]: { question: questionText, recruiter_note: value },
				},
			}));
			schedule();
		},
		[schedule]
	);

	const updateGeneralNotes = useCallback(
		(value: string) => {
			setGeneralNotes(value);
			schedule();
		},
		[schedule]
	);

	// Parent calls this on every render with the current (non-snapshotted)
	// question list derived from ai_analysis. The value is only used to seed
	// the snapshot on the first write.
	const setActiveQuestions = useCallback((list: InterviewPrepQuestion[]) => {
		activeQuestionsRef.current = list;
	}, []);

	// Adds questions from freshList whose TEXT does not already appear in the
	// snapshot. The AI always reuses question1…question10 as keys even when
	// generating completely new questions after a re-run, so we compare by
	// text rather than by key to detect genuinely new content.
	// New questions are renumbered starting after the current highest index to
	// avoid key collisions with the existing snapshot.
	const loadMoreQuestions = useCallback(
		async (freshList: InterviewPrepQuestion[]) => {
			const currentSnapshot = notesRef.current.questions_snapshot || [];
			const existingTexts = new Set(currentSnapshot.map((q) => q.text));
			const maxIndex = currentSnapshot.reduce((m, q) => Math.max(m, q.index), 0);

			const newOnes = freshList.filter((q) => !existingTexts.has(q.text));
			if (newOnes.length === 0) return;

			// Remap to unique keys/indices so they don't collide with the existing snapshot.
			const remapped: InterviewPrepQuestion[] = newOnes.map((q, i) => ({
				text: q.text,
				key: `question${maxIndex + i + 1}`,
				index: maxIndex + i + 1,
			}));

			const updatedSnapshot = [...currentSnapshot, ...remapped];
			const updated: InterviewPrepNotes = {
				...notesRef.current,
				questions_snapshot: updatedSnapshot,
			};
			notesRef.current = updated;
			setNotes(updated);
			setQuestionsSnapshot(updatedSnapshot);
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
				debounceTimer.current = null;
			}
			await flush();
		},
		[flush]
	);

	// Permanently removes a question from the snapshot and deletes its notes.
	// Re-assigns display indices so the numbering stays contiguous.
	// Unlike ignoreQuestion, the key is NOT added to ignored_questions, so
	// "Load more" can resurface an equivalent question from a future re-analysis.
	const deleteQuestion = useCallback(
		async (key: string) => {
			const remaining = (notesRef.current.questions_snapshot || [])
				.filter((q) => q.key !== key)
				.map((q, i) => ({ ...q, index: i + 1 }));

			const updatedQuestions = { ...(notesRef.current.questions || {}) };
			delete updatedQuestions[key];

			const updated: InterviewPrepNotes = {
				...notesRef.current,
				questions_snapshot: remaining,
				questions: updatedQuestions,
			};
			notesRef.current = updated;
			setNotes(updated);
			setQuestionsSnapshot(remaining);
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
				debounceTimer.current = null;
			}
			await flush();
		},
		[flush]
	);

	// Removes a question from the visible snapshot and records it as ignored so
	// "Load more" does not re-add it. Persists via the normal debounce.
	const ignoreQuestion = useCallback(
		(key: string) => {
			const updatedSnapshot = (notesRef.current.questions_snapshot || []).filter(
				(q) => q.key !== key
			);
			const ignored = [
				...(notesRef.current.ignored_questions || []),
				key,
			];
			const updated: InterviewPrepNotes = {
				...notesRef.current,
				questions_snapshot: updatedSnapshot,
				ignored_questions: ignored,
			};
			notesRef.current = updated;
			setNotes(updated);
			setQuestionsSnapshot(updatedSnapshot);
			schedule();
		},
		[schedule]
	);

	// Explicit user-initiated regeneration. Clears all recruiter notes and
	// re-snapshots from the provided fresh list. Flushes immediately.
	const regenerateQuestions = useCallback(
		async (freshList: InterviewPrepQuestion[]) => {
			const cleared: InterviewPrepNotes = {
				...notesRef.current,
				questions: {},
				questions_snapshot: freshList,
			};
			notesRef.current = cleared;
			activeQuestionsRef.current = freshList;
			setNotes(cleared);
			setQuestionsSnapshot(freshList);
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
				debounceTimer.current = null;
			}
			await flush();
		},
		[flush]
	);

	return {
		loading,
		saveState,
		notes,
		generalNotes,
		questionsSnapshot,
		updatedAt,
		setActiveQuestions,
		regenerateQuestions,
		loadMoreQuestions,
		ignoreQuestion,
		deleteQuestion,
		updateQuestionNote,
		updateGeneralNotes,
	};
};
